/**
 * Eigen invoer over een generatienaad heen (ontwerp 4.6 en 9.2, eisenboek 12.50).
 *
 * Na een compactie gooit de pagina haar sectiedocument weg en haalt ze het opnieuw op: het oude document
 * heeft de items van de vorige generatie, en wie daarmee verbindt brengt die generatie de nieuwe in. Wat de
 * invuller typte en de pod nog niet had (een aanslag tijdens de compactie, of werk van een laptop die
 * sliep), staat alleen in dat oude document. Deze module haalt het eruit en zet het als een gewone, nieuwe
 * wijziging in het nieuwe document, of zegt welke velden dat niet kon.
 *
 * **Drie standen per veld.** `bij de naad`: wat de pod had toen de generatie sloot, uit de eindsnapshot
 * die hij meestuurt, beperkt tot wat deze browser kent. `lokaal`: wat deze browser heeft, met de
 * verwijderingen die de pod had erbij (anders telt de verwijdering van een ander die deze browser miste als
 * eigen invoer). `nieuw`: de nieuwe generatie zoals ze nu binnenkwam. Een veld waar lokaal en bij de naad
 * gelijk zijn, heeft geen eigen invoer. Is nieuw nog gelijk aan bij de naad, dan heeft niemand het
 * sindsdien geraakt en komt het lokale antwoord erin. Anders schreef een ander er intussen in, of had de
 * pod er tekst die deze browser nooit kreeg: dan zet de pagina niets over het werk van een ander heen, en
 * krijgt de invuller zijn tekst om te kopiëren (`nietTeruggezet`).
 *
 * **Wat er niet mee gaat, net als bij de naad zelf.** Het opmerkinganker in de tekst: de pod zaait de nieuwe
 * generatie zonder (`structureOf`), en een anker dat hier terugkwam, zou een markering kunnen zijn zonder
 * draadje erachter.
 *
 * **Alleen het verschil, en niet het veld als geheel.** Wat deze browser terugzet is een nieuwe wijziging
 * onder zijn client, en de pod koppelt die client aan wie verbonden is (R-154, R-188, eisenboek 12.49). Wat
 * in het nieuwe document al klopt, blijft het item dat het was, met zijn eigen client: de tekst die de pod
 * zaaide en die van medeschrijvers komt dus niet op naam van wie terugkwam (`copyField`).
 */
import { updateYFragment } from '@tiptap/y-tiptap';
import * as Y from 'yjs';
import { COMMENT_MARK } from '../comments/CommentMark.js';
import { richTextSchema } from '../field/controls/richtext/schema.js';
import { addSource, fieldText, isMergedText, recordOrigin, replaceText, richtextFragment, rowsList, sourcesOf, textOf, valuesMap } from './sectionDocument.js';
import { answerOf, structureOf } from './state.js';
/**
 * Zet de eigen invoer die de pod bij de naad niet had, in het nieuwe document (zie de kop).
 *
 * Zonder eindsnapshot weet de pagina niet wat de pod had. Dan zet ze niets terug, en noemt ze elk veld
 * waarin oud en nieuw verschillen: liever een melding te veel dan iets over het werk van een ander heen.
 */
export function carryOverAcrossSeam(input) {
    const restored = [];
    const notRestored = [];
    const fields = input.sectie.velden.filter((field) => input.overslaan?.has(field.id) !== true);
    if (input.eindsnapshot === undefined) {
        for (const field of fields) {
            if (!sameAnswer(answerOf({ veld: field, doc: input.oud }), answerOf({ veld: field, doc: input.nieuw })))
                notRestored.push(field.id);
        }
        return { teruggezet: restored, nietTeruggezet: notRestored };
    }
    const { atSeam, local } = threeStates(input.oud, input.eindsnapshot);
    try {
        input.nieuw.transact(() => {
            for (const field of fields) {
                const own = answerOf({ veld: field, doc: local });
                const seam = answerOf({ veld: field, doc: atSeam });
                if (sameAnswer(own, seam))
                    continue;
                const fresh = answerOf({ veld: field, doc: input.nieuw });
                if (sameAnswer(own, fresh))
                    continue;
                if (!sameAnswer(fresh, seam)) {
                    notRestored.push(field.id);
                    continue;
                }
                copyField(field, local, input.nieuw, input.herkomst);
                restored.push(field.id);
            }
            if (restored.length > 0)
                carrySources(local, atSeam, input.nieuw);
        });
    }
    finally {
        atSeam.destroy();
        local.destroy();
    }
    return { teruggezet: restored, nietTeruggezet: notRestored };
}
/** De stand bij de naad en de lokale stand, allebei uit het oude document (zie de kop). */
function threeStates(old, encodedSnapshot) {
    const seam = Y.decodeSnapshot(encodedSnapshot);
    const known = Y.decodeStateVector(Y.encodeStateVector(old));
    // Wat de pod had, maar alleen tot waar deze browser het kent: `createDocFromSnapshot` kan geen items maken
    // die hier niet zijn, en wat de pod meer had, had de browser ook niet in zijn eigen invoer.
    const seamKnown = new Map();
    for (const [client, clock] of seam.sv) {
        const upTo = Math.min(clock, known.get(client) ?? 0);
        if (upTo > 0)
            seamKnown.set(client, upTo);
    }
    const atSeam = Y.createDocFromSnapshot(old, Y.createSnapshot(seam.ds, seamKnown), new Y.Doc({ gc: false }));
    const deletions = Y.mergeDeleteSets([Y.createDeleteSetFromStructStore(old.store), seam.ds]);
    const local = Y.createDocFromSnapshot(old, Y.createSnapshot(deletions, known), new Y.Doc({ gc: false }));
    return { atSeam, local };
}
/** Twee antwoorden met dezelfde inhoud; wie en wanneer tellen niet mee, want die stempelt de pod. */
function sameAnswer(a, b) {
    return JSON.stringify(withoutOrigin(a)) === JSON.stringify(withoutOrigin(b));
}
function withoutOrigin(value) {
    if (Array.isArray(value))
        return value.map(withoutOrigin);
    if (value === null || typeof value !== 'object')
        return value;
    const out = {};
    for (const [key, inner] of Object.entries(value)) {
        if (key === 'door' || key === 'gewijzigdOp')
            continue;
        out[key] = withoutOrigin(inner);
    }
    return out;
}
/**
 * Het lokale antwoord van dit veld in het nieuwe document, op de plek waar het control het leest (6.8), en
 * alleen het verschil. Wat in het nieuwe document al gelijk is, blijft het item dat het was: zo houden de
 * tekst die de pod zaaide en die van medeschrijvers hun eigen client in de auteursmap (R-188, eisenboek
 * 12.49), en komt alleen wat deze browser zelf typte onder zijn client. Een veld als geheel vervangen zette
 * alle tekst van dat veld op naam van wie terugkwam, en dat is precies wat 12.50 herstelt.
 */
function copyField(field, from, to, origin) {
    if ('rijVelden' in field) {
        syncRows(rowsList(from, field.id), rowsList(to, field.id), to);
        return;
    }
    if (field.control === 'richtext') {
        syncFragment(to, richtextFragment(from, field.id), richtextFragment(to, field.id));
        recordOrigin(to, field.id, origin);
        return;
    }
    if (isMergedText(field.control)) {
        replaceText(fieldText(to, field.id), textOf(from, field.id));
        recordOrigin(to, field.id, origin);
        return;
    }
    const value = valuesMap(from).get(field.id);
    if (value === undefined)
        valuesMap(to).delete(field.id);
    else
        valuesMap(to).set(field.id, structuredClone(value));
    recordOrigin(to, field.id, origin);
}
/**
 * Een rich-text-fragment op de structuur van een ander zetten, met de diff die de editor zelf gebruikt
 * (`updateYFragment`): een alinea die gelijk is blijft staan, in een gewijzigde alinea verandert alleen de
 * tekst die anders is. De structuur zonder opmerkinganker, zoals de pod een generatie zaait (`structureOf`).
 */
function syncFragment(to, from, into) {
    const node = richTextSchema().nodeFromJSON(structureOf(from));
    updateYFragment(to, into, node, { mapping: new Map(), isOMark: new Map() });
}
/**
 * De rijen van een herhaalgroep per rij-id: een rij die lokaal weg is gaat weg, een nieuwe rij komt erbij, en
 * in een rij die er is verandert alleen de cel die anders is (R-40, 6.8).
 */
function syncRows(from, into, to) {
    const wanted = new Map(from.toArray().map((row) => [String(row.get('rijId')), row]));
    for (let index = into.length - 1; index >= 0; index--) {
        if (!wanted.has(String(into.get(index).get('rijId'))))
            into.delete(index, 1);
    }
    const present = new Map(into.toArray().map((row) => [String(row.get('rijId')), row]));
    for (const [rowId, row] of wanted) {
        const target = present.get(rowId);
        if (target === undefined) {
            // Een rij die alleen deze browser had: die is van hem, dus als kopie, zonder opmerkinganker.
            const copy = row.clone();
            into.push([copy]);
            withoutCommentMarks(copy);
            continue;
        }
        syncRow(row, target, to);
    }
}
function syncRow(from, into, to) {
    for (const key of [...into.keys()])
        if (!from.has(key))
            into.delete(key);
    for (const [key, value] of from.entries()) {
        const current = into.get(key);
        if (value instanceof Y.Text) {
            if (current instanceof Y.Text)
                replaceText(current, value.toString());
            else
                into.set(key, new Y.Text(value.toString()));
        }
        else if (value instanceof Y.XmlFragment) {
            if (current instanceof Y.XmlFragment)
                syncFragment(to, value, current);
            else {
                const created = new Y.XmlFragment();
                into.set(key, created);
                syncFragment(to, value, created);
            }
        }
        else if (JSON.stringify(current) !== JSON.stringify(value)) {
            into.set(key, structuredClone(value));
        }
    }
}
/** Haalt elk opmerkinganker uit dit type en alles eronder (zie de kop). Alleen op een type in een document. */
function withoutCommentMarks(type) {
    if (type instanceof Y.XmlText || type instanceof Y.Text) {
        let index = 0;
        for (const op of type.toDelta()) {
            const length = typeof op.insert === 'string' ? op.insert.length : 1;
            const marks = Object.keys(op.attributes ?? {}).filter((key) => key === COMMENT_MARK || key.startsWith(`${COMMENT_MARK}--`));
            if (marks.length > 0)
                type.format(index, length, Object.fromEntries(marks.map((key) => [key, null])));
            index += length;
        }
        return;
    }
    if (type instanceof Y.XmlFragment || type instanceof Y.XmlElement) {
        for (const child of type.toArray())
            withoutCommentMarks(child);
        return;
    }
    if (type instanceof Y.Map) {
        for (const value of type.values())
            withoutCommentMarks(value);
        return;
    }
    if (type instanceof Y.Array) {
        for (const value of type.toArray())
            withoutCommentMarks(value);
    }
}
/** Een bron die de teruggezette tekst citeert en die alleen hier stond, gaat mee (R-50). */
function carrySources(local, atSeam, to) {
    const present = new Set(sourcesOf(to).map((source) => source.id));
    const before = new Set(sourcesOf(atSeam).map((source) => source.id));
    for (const source of sourcesOf(local)) {
        if (!present.has(source.id) && !before.has(source.id))
            addSource(to, source);
    }
}
//# sourceMappingURL=seam.js.map